import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCheck, LogOut, Users, CheckCircle, BarChart3, AlertCircle, Copy, Check, Key, Shield, Clock, XCircle, ThumbsUp, ThumbsDown, Trash2, MoreHorizontal } from 'lucide-react';
import { useCounselor } from '../hooks/useCounselor';
import { signOut } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { approveCouple, rejectCouple, deleteCouple } from '../services/coupleService';
import LoadingSpinner from '../components/LoadingSpinner';

const CounselorDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, userRole } = useAuth();
  const { couples, loading, updateSharing, saveNotes, getNotes, reload } = useCounselor();
  const [selectedCouple, setSelectedCouple] = useState(null);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [processingApproval, setProcessingApproval] = useState(false);
  const [expandedCoupleId, setExpandedCoupleId] = useState(null);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleViewDetails = (couple) => {
    console.log('=== VIEW DETAILS CLICKED ===');
    console.log('Couple object:', couple);
    console.log('Available properties:', Object.keys(couple));
    console.log('couple.id:', couple.id);
    console.log('couple.coupleId:', couple.coupleId);
    console.log('Current userRole:', userRole);
  
    const coupleId = couple.id || couple.coupleId || couple.docId;
    
    if (!coupleId) {
      console.error('❌ ERROR: No couple ID found in couple object!');
      alert('Error: Cannot load couple details. Missing couple ID.');
      return;
    }
    
    const targetUrl = `/counselor/couple/${coupleId}`;
    console.log('✅ Navigating to:', targetUrl);
    
    navigate(targetUrl);
  };

  const handleToggleSharing = async (coupleId, currentStatus) => {
    await updateSharing(coupleId, !currentStatus);
  };

  const handleSaveNotes = async () => {
    if (!selectedCouple) return;
    setSavingNotes(true);
    await saveNotes(selectedCouple.id, notes);
    setSavingNotes(false);
    alert('Notes saved successfully!');
  };

  const handleCopyCode = () => {
    if (userProfile?.counselorCode) {
      navigator.clipboard.writeText(userProfile.counselorCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const handleApprove = async (coupleId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Approve this couple? They will be able to take the assessment.')) return;
    
    setProcessingApproval(true);
    const result = await approveCouple(coupleId);
    
    if (result.success) {
      alert('Couple approved successfully!');
      await reload();
    } else {
      alert('Error approving couple: ' + result.error);
    }
    setProcessingApproval(false);
  };

  const handleReject = async (coupleId) => {
    // eslint-disable-next-line no-restricted-globals
    const reason = prompt('Optional: Enter reason for rejection (will be shown to the couple):');
    if (reason === null) return;
    
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Reject this couple? They will not be able to take the assessment.')) return;
    
    setProcessingApproval(true);
    const result = await rejectCouple(coupleId, reason);
    
    if (result.success) {
      alert('Couple rejected.');
      await reload();
    } else {
      alert('Error rejecting couple: ' + result.error);
    }
    setProcessingApproval(false);
  };

  const handleDelete = async (coupleId, coupleName) => {
    // eslint-disable-next-line no-restricted-globals
    const confirmed = confirm(
      `⚠️ DELETE COUPLE\n\nAre you sure you want to permanently delete ${coupleName}?\n\nThis will remove:\n• Both partner accounts\n• All assessment data\n• All progress\n\nThis action CANNOT be undone!`
    );
    
    if (!confirmed) return;
    
    // eslint-disable-next-line no-restricted-globals
    const doubleConfirm = confirm(
      `FINAL CONFIRMATION\n\nClick OK to permanently delete ${coupleName} and all their data.`
    );
    
    if (!doubleConfirm) return;
    
    setProcessingApproval(true);
    const result = await deleteCouple(coupleId);
    
    if (result.success) {
      alert('Couple deleted successfully.');
      setExpandedCoupleId(null);
      await reload();
    } else {
      alert('Error deleting couple: ' + result.error);
    }
    setProcessingApproval(false);
  };

  const toggleDeleteOption = (coupleId) => {
    if (expandedCoupleId === coupleId) {
      setExpandedCoupleId(null);
    } else {
      setExpandedCoupleId(coupleId);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const pendingCouples = couples.filter(c => c.approvalStatus === 'pending');
  const approvedCouples = couples.filter(c => c.approvalStatus === 'approved');
  const rejectedCouples = couples.filter(c => c.approvalStatus === 'rejected');
  
  const completedCount = approvedCouples.filter(c => c.overallStatus === 'completed').length;
  const inProgressCount = approvedCouples.filter(c => c.overallStatus === 'in-progress').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <UserCheck className="w-6 h-6 text-blue-600" />
            <span className="font-semibold text-lg">Counselor Dashboard</span>
          </div>
          <button 
            onClick={handleSignOut}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Welcome, {userProfile?.name || 'Counselor'}</h1>
          <p className="text-gray-600">Manage and monitor couple assessments</p>
        </div>

        {userProfile?.counselorCode && (
          <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border-2 border-blue-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <Key className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Your Counselor Code</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Share this code with couples so they can register under your guidance
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 p-4 bg-white rounded-lg border-2 border-blue-300 shadow-sm">
                    <code className="text-2xl font-bold text-blue-600 tracking-wider">
                      {userProfile.counselorCode}
                    </code>
                  </div>
                  <button
                    onClick={handleCopyCode}
                    className="p-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                    title="Copy to clipboard"
                  >
                    {codeCopied ? (
                      <>
                        <Check className="w-5 h-5" />
                        <span className="text-sm font-medium">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-5 h-5" />
                        <span className="text-sm font-medium">Copy</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
              <Shield className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <strong>Security:</strong> All new registrations require your approval before couples can take assessments.
              </div>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Couples</p>
                <p className="text-3xl font-bold mt-1">{approvedCouples.length}</p>
              </div>
              <Users className="w-10 h-10 text-blue-600 opacity-20" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Pending Approval</p>
                <p className="text-3xl font-bold mt-1 text-orange-600">{pendingCouples.length}</p>
              </div>
              <Clock className="w-10 h-10 text-orange-600 opacity-20" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Completed</p>
                <p className="text-3xl font-bold mt-1">{completedCount}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600 opacity-20" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">In Progress</p>
                <p className="text-3xl font-bold mt-1">{inProgressCount}</p>
              </div>
              <BarChart3 className="w-10 h-10 text-yellow-600 opacity-20" />
            </div>
          </div>
        </div>

        {pendingCouples.length > 0 && (
          <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-6 h-6 text-orange-600" />
              <h2 className="text-xl font-semibold text-orange-900">Pending Approval ({pendingCouples.length})</h2>
            </div>
            <p className="text-sm text-orange-800 mb-4">
              These couples are waiting for your approval before they can take the assessment
            </p>
            <div className="space-y-3">
              {pendingCouples.map(couple => (
                <div key={couple.id} className="bg-white p-4 rounded-lg border-2 border-orange-200">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-gray-400" />
                      <div>
                        <h3 className="font-semibold">
                          {couple.partner1Name} & {couple.partner2Name}
                        </h3>
                        <p className="text-xs text-gray-600">
                          Registered: {couple.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApprove(couple.id)}
                        disabled={processingApproval}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition disabled:opacity-50 flex items-center gap-2"
                      >
                        <ThumbsUp className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => handleReject(couple.id)}
                        disabled={processingApproval}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition disabled:opacity-50 flex items-center gap-2"
                      >
                        <ThumbsDown className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-6 border-b">
            <h2 className="text-xl font-semibold">Approved Couples ({approvedCouples.length})</h2>
          </div>
          <div className="divide-y">
            {approvedCouples.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <p className="mb-2">No approved couples yet</p>
                <p className="text-sm">Approve pending couples to see them here</p>
              </div>
            ) : (
              approvedCouples.map(couple => (
                <div key={couple.id} className="p-6 hover:bg-gray-50 transition">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-gray-400" />
                      <div>
                        <h3 className="font-semibold">
                          {couple.partner1Name} & {couple.partner2Name}
                        </h3>
                        <p className="text-sm text-gray-600">
                          Status: {couple.overallStatus === 'completed' ? '✅ Completed' : 
                                  couple.overallStatus === 'in-progress' ? '⏳ In Progress' : 
                                  '⭕ Not Started'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewDetails(couple)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition"
                      >
                        View Details
                      </button>
                      
                      <div className="relative flex items-center">
                        <div 
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            expandedCoupleId === couple.id ? 'max-w-[100px] opacity-100 mr-1' : 'max-w-0 opacity-0'
                          }`}
                        >
                          <button
                            onClick={() => handleDelete(couple.id, `${couple.partner1Name} & ${couple.partner2Name}`)}
                            disabled={processingApproval}
                            className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition disabled:opacity-50 flex items-center gap-1 whitespace-nowrap"
                            title="Delete couple permanently"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                        
                        <button
                          onClick={() => toggleDeleteOption(couple.id)}
                          className={`p-2 rounded-lg text-sm transition ${
                            expandedCoupleId === couple.id 
                              ? 'bg-gray-200 text-gray-700' 
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                          }`}
                          title={expandedCoupleId === couple.id ? "Hide options" : "More options"}
                        >
                          <MoreHorizontal className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {couple.overallStatus === 'completed' && (
                    <div className="flex items-center gap-4 mt-4 p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-700">Result Sharing:</span>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={couple.sharingEnabled || false}
                          onChange={() => handleToggleSharing(couple.id, couple.sharingEnabled)}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm">Allow couple to view each other's results</span>
                      </label>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {rejectedCouples.length > 0 && (
          <details className="mt-8 bg-white rounded-xl shadow-sm border">
            <summary className="p-6 cursor-pointer hover:bg-gray-50 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              <span className="font-semibold">Rejected Couples ({rejectedCouples.length})</span>
            </summary>
            <div className="p-6 pt-0 space-y-3">
              {rejectedCouples.map(couple => (
                <div key={couple.id} className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <h3 className="font-semibold text-red-900">
                        {couple.partner1Name} & {couple.partner2Name}
                      </h3>
                      {couple.rejectionReason && (
                        <p className="text-xs text-red-700 mt-1">
                          Reason: {couple.rejectionReason}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleApprove(couple.id)}
                        className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                      >
                        Re-approve
                      </button>
                      
                      <div className="relative flex items-center">
                        <div 
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            expandedCoupleId === couple.id ? 'max-w-[80px] opacity-100 mr-1' : 'max-w-0 opacity-0'
                          }`}
                        >
                          <button
                            onClick={() => handleDelete(couple.id, `${couple.partner1Name} & ${couple.partner2Name}`)}
                            disabled={processingApproval}
                            className="px-2 py-1 bg-red-700 text-white rounded text-xs hover:bg-red-800 disabled:opacity-50 flex items-center gap-1 whitespace-nowrap"
                          >
                            <Trash2 className="w-3 h-3" />
                            Delete
                          </button>
                        </div>
                        
                        <button
                          onClick={() => toggleDeleteOption(couple.id)}
                          className={`p-1 rounded text-xs transition ${
                            expandedCoupleId === couple.id 
                              ? 'bg-red-200 text-red-700' 
                              : 'bg-red-100 text-red-500 hover:bg-red-200 hover:text-red-700'
                          }`}
                          title={expandedCoupleId === couple.id ? "Hide options" : "More options"}
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {selectedCouple && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold">
                {selectedCouple.partner1Name} & {selectedCouple.partner2Name}
              </h2>
              <button
                onClick={() => setSelectedCouple(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                {selectedCouple.partner1Assessment?.scores && selectedCouple.partner2Assessment?.scores ? (
                  <>
                    <div>
                      <h3 className="font-semibold mb-3">Individual Scores</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <p className="text-sm text-gray-600 mb-1">{selectedCouple.partner1Name}'s Total</p>
                          <p className="text-2xl font-bold text-blue-600">
                            {selectedCouple.partner1Assessment.scores.total}/250
                          </p>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-lg">
                          <p className="text-sm text-gray-600 mb-1">{selectedCouple.partner2Name}'s Total</p>
                          <p className="text-2xl font-bold text-purple-600">
                            {selectedCouple.partner2Assessment.scores.total}/250
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5" />
                      One or both partners have not completed their assessment yet.
                    </p>
                  </div>
                )}

                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-semibold mb-2">Counselor Notes</h4>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full p-3 border rounded-lg text-sm"
                    rows="4"
                    placeholder="Add private notes..."
                  ></textarea>
                  <button 
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                    className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savingNotes ? 'Saving...' : 'Save Notes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CounselorDashboard;
